import type {
  ContentDefinition,
  ForDefinition,
  ImportBinding,
  PluginDefinition,
  UseBinding,
} from "@magic-md/core";
import {
  buildContentExecutionEntry,
  buildDefinitionExecutionEntry,
  buildForExecutionEntry,
  buildImportExecutionEntry,
  buildUseExecutionEntry,
} from "./execution-entry-builders";
import type {
  ContentExecutionEntry,
  ExecutionEntry,
  ForExecutionEntry,
  ImportExecutionEntry,
  UseExecutionEntry,
} from "./execution-state";

export interface ParsedOutputExecutionState {
  executions: Map<string, ExecutionEntry>;
  forExecutions: Map<string, ForExecutionEntry>;
  contentExecutions: Map<string, ContentExecutionEntry>;
  importExecutions: Map<string, ImportExecutionEntry>;
  useExecutions: Map<string, UseExecutionEntry>;
  engineDefinitions: Map<string, PluginDefinition>;
  engineContentDefinitions: Map<string, ContentDefinition>;
}

export interface ParsedOutputRestoreInput {
  definitions: Map<string, PluginDefinition>;
  forDefinitions?: Map<string, ForDefinition>;
  uses?: UseBinding[];
  imports?: ImportBinding[];
  contentDefinitions?: Map<string, ContentDefinition>;
}

export type ParsedOutputExecutionPatch = Partial<
  Pick<
    ParsedOutputExecutionState,
    | "executions"
    | "forExecutions"
    | "contentExecutions"
    | "importExecutions"
    | "useExecutions"
  >
>;

export function createParsedOutputExecutionPatch(
  state: ParsedOutputExecutionState,
  input: ParsedOutputRestoreInput,
): ParsedOutputExecutionPatch {
  const patch: ParsedOutputExecutionPatch = {};
  const { definitions, forDefinitions, uses, imports, contentDefinitions } =
    input;

  const executions = new Map(state.executions);
  let executionsChanged = false;
  for (const [id, definition] of definitions) {
    const existing = state.executions.get(id);
    if (existing) {
      if (existing.status === "running" && definition.output?.__metadata) {
        const meta = definition.output.__metadata as unknown as Record<
          string,
          unknown
        >;
        executions.set(
          id,
          buildDefinitionExecutionEntry(id, definition.output, {
            actionName: (meta.actionName as string) ?? existing.actionName,
            startedAt: existing.startedAt,
            duration: (meta.duration as number) ?? null,
          }),
        );
        executionsChanged = true;
      }
      continue;
    }
    if (!definition.output) continue;
    const meta = definition.output.__metadata;
    const error = definition.output.__error;
    if (!meta && !error) continue;
    executions.set(
      id,
      buildDefinitionExecutionEntry(id, definition.output, {
        actionName: meta?.actionName ?? error?.actionName ?? null,
        duration: meta?.duration ?? null,
      }),
    );
    executionsChanged = true;
  }

  const useIds = new Set<string>();
  for (const use of uses ?? []) {
    useIds.add(use.id);
    if (executions.has(use.id) || !use.output) continue;
    const cached = use.output;
    if (cached.kind !== "node" || !cached.nodeOutput) continue;
    const meta = cached.nodeOutput.__metadata as
      | Record<string, unknown>
      | undefined;
    const error = (cached.nodeOutput as Record<string, unknown>).__error as
      | { message: string; actionName?: string }
      | undefined;
    if (!meta && !error) continue;
    executions.set(
      use.id,
      buildDefinitionExecutionEntry(use.id, cached.nodeOutput, {
        actionName: (meta?.actionName as string) ?? error?.actionName ?? null,
        duration: (meta?.duration as number) ?? null,
      }),
    );
    executionsChanged = true;
  }

  const importIds = new Set<string>();
  for (const binding of imports ?? []) {
    if (binding.output?.symbols) {
      for (const symbol of binding.output.symbols) {
        importIds.add(symbol.qualifiedId);
      }
      for (const symbol of binding.output.instanceSymbols ?? []) {
        importIds.add(symbol.qualifiedId);
      }
    } else if (!binding.output) {
      const prefix = `${binding.namespace}/`;
      for (const id of state.executions.keys()) {
        if (
          (id === binding.namespace || id.startsWith(prefix)) &&
          state.engineDefinitions.has(id)
        ) {
          importIds.add(id);
        }
      }
    }
  }
  for (const id of state.executions.keys()) {
    if (!definitions.has(id) && !useIds.has(id) && !importIds.has(id)) {
      executions.delete(id);
      executionsChanged = true;
    }
  }
  if (executionsChanged) patch.executions = executions;

  if (imports && imports.length > 0) {
    const next = new Map(state.importExecutions);
    let changed = false;
    for (const binding of imports) {
      if (state.importExecutions.has(binding.namespace)) continue;
      const document = binding.document ? binding.output?.document : undefined;
      if (
        !binding.output ||
        ((binding.output.symbols?.length ?? 0) === 0 && !document)
      ) {
        continue;
      }
      next.set(
        binding.namespace,
        buildImportExecutionEntry(
          binding.namespace,
          (binding.output.symbols ?? []).map((symbol) => ({
            qualifiedId: symbol.qualifiedId,
            kind: symbol.kind,
            publicName: symbol.publicName,
          })),
          {
            ...(document ? { document } : {}),
            meta: {
              total: binding.output.symbols?.length ?? 0,
              duration: 0,
              source: binding.source,
            },
          },
        ),
      );
      changed = true;
    }
    const activeNamespaces = new Set(
      imports.map((binding) => binding.namespace),
    );
    for (const namespace of state.importExecutions.keys()) {
      if (!activeNamespaces.has(namespace)) {
        next.delete(namespace);
        changed = true;
      }
    }
    if (changed) patch.importExecutions = next;
  }

  if (uses && uses.length > 0) {
    const next = new Map(state.useExecutions);
    let changed = false;
    for (const use of uses) {
      if (state.useExecutions.has(use.id) || !use.output) continue;
      next.set(
        use.id,
        buildUseExecutionEntry(use.id, {
          materializedId: use.output.materializedId ?? use.id,
          sourceName: use.output.sourceName ?? use.source,
          kind: use.output.kind,
          materializedBody: use.output.materializedBody,
        }),
      );
      changed = true;
    }
    const activeUseIds = new Set(uses.map((use) => use.id));
    for (const id of state.useExecutions.keys()) {
      if (!activeUseIds.has(id)) {
        next.delete(id);
        changed = true;
      }
    }
    if (changed) patch.useExecutions = next;
  }

  const hasContentUses =
    uses?.some((use) => use.output?.kind === "content") ?? false;
  if ((contentDefinitions?.size ?? 0) > 0 || hasContentUses) {
    const next = new Map(state.contentExecutions);
    let changed = false;
    for (const [contentId, definition] of contentDefinitions ?? []) {
      if (state.contentExecutions.has(contentId) || !definition.output) {
        continue;
      }
      const total = Object.keys(definition.output).length;
      next.set(
        contentId,
        buildContentExecutionEntry(contentId, definition.output, {
          meta: {
            total,
            succeeded: total,
            failed: 0,
            duration: 0,
            startedAt: "",
          },
        }),
      );
      changed = true;
    }
    for (const use of uses ?? []) {
      const cached = use.output;
      if (
        cached?.kind !== "content" ||
        !cached.contentOutput ||
        next.has(use.id)
      ) {
        continue;
      }
      const total = Object.keys(cached.contentOutput).length;
      next.set(
        use.id,
        buildContentExecutionEntry(use.id, cached.contentOutput, {
          meta: {
            total,
            succeeded: total,
            failed: 0,
            duration: 0,
            startedAt: "",
          },
        }),
      );
      changed = true;
    }

    const validIds = new Set(contentDefinitions?.keys() ?? []);
    for (const use of uses ?? []) {
      if (use.output?.kind === "content") {
        validIds.add(use.id);
      } else if (use.externalRef && state.contentExecutions.has(use.id)) {
        validIds.add(use.id);
      }
    }
    for (const id of state.contentExecutions.keys()) {
      if (state.engineContentDefinitions.has(id)) validIds.add(id);
      if (!validIds.has(id)) {
        next.delete(id);
        changed = true;
      }
    }
    if (changed) patch.contentExecutions = next;
  }

  const hasForUses = uses?.some((use) => use.output?.kind === "for") ?? false;
  if ((forDefinitions?.size ?? 0) > 0 || hasForUses) {
    const next = new Map(state.forExecutions);
    let changed = false;
    for (const [forId, definition] of forDefinitions ?? []) {
      if (state.forExecutions.has(forId) || !definition.output) continue;
      next.set(forId, buildForExecutionEntry(forId, definition.output));
      changed = true;
    }
    for (const use of uses ?? []) {
      const cached = use.output;
      if (cached?.kind !== "for" || !cached.forOutput || next.has(use.id)) {
        continue;
      }
      next.set(use.id, buildForExecutionEntry(use.id, cached.forOutput));
      changed = true;
    }

    const validIds = new Set(forDefinitions?.keys() ?? []);
    for (const use of uses ?? []) {
      if (use.output?.kind === "for") {
        validIds.add(use.id);
      } else if (use.externalRef && state.forExecutions.has(use.id)) {
        validIds.add(use.id);
      }
    }
    for (const id of state.forExecutions.keys()) {
      if (!validIds.has(id)) {
        next.delete(id);
        changed = true;
      }
    }
    if (changed) patch.forExecutions = next;
  }

  return patch;
}
